import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { UpdateAccountRequestDTO } from '@/core/api/dtos/request/account/update'

import apiClient from '@core/api/client'
import { SaveBillingDataRequestDTO } from '@core/api/dtos/request/account/saveBillingData'
import { SaveBillingInformationRequestDTO } from '@core/api/dtos/request/account/saveBillingInformation'
import { SavePaymentMethodRequestDTO } from '@core/api/dtos/request/account/savePaymentMethod'
import { UpdateBrandProfileRequestDTO } from '@core/api/dtos/request/account/updateBrandProfile'

export const accountService = {
	/**
	 * Get an account by id
	 */
	getAccount(
		accountId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient
			.get(`/my/accounts/${accountId}`, config)
			.then((response) => {
				return response
			})
	},

	/**
	 * Update brand profile
	 */
	updateBrandProfile(
		accountId: number,
		data: UpdateBrandProfileRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient
			.patch(`/my/accounts/${accountId}`, data, config)
			.then((response) => {
				return response
			})
	},

	/**
	 * Update account
	 */
	updateAccount(
		accountId: number,
		data: UpdateAccountRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient
			.patch(`/my/accounts/${accountId}`, data, config)
			.then((response) => {
				return response
			})
	},

	/**
	 * Save payment method
	 */
	savePaymentMethod(
		accountId: number,
		data: SavePaymentMethodRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(
			`/my/accounts/${accountId}/payment-methods`,
			data,
			config
		)
	},

	/**
	 * Save billing information
	 */
	saveBillingInformation(
		accountId: number,
		data: SaveBillingInformationRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/my/accounts/${accountId}`, data, config)
	},

	/**
	 * Save billing data
	 */
	saveBillingData(
		accountId: number,
		data: SaveBillingDataRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/accounts/${accountId}/billing_data`, data, config)
	},

	/**
	 * Get users for the account
	 */
	getUsers(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/accounts/users', config)
	},

	/**
	 * Paginate accounts with search (for manage_children_accounts permission)
	 */
	paginateAccounts(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/accounts', config)
	},

	/**
	 * List accounts (for list_accounts permission)
	 */
	listAccounts(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/accounts', config)
	},

	/**
	 * Get invoices with pagination
	 */
	getInvoices(
		accountId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/accounts/${accountId}/invoices`, config)
	},

	/**
	 * Download invoice PDF
	 */
	downloadInvoice(
		invoiceId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/accounts/invoices/${invoiceId}/download`, {
			...config,
			responseType: 'blob',
		})
	},

	/**
	 * Download invoice XML
	 */
	downloadInvoiceXML(
		invoiceId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/accounts/invoices/${invoiceId}/downloadXML`, {
			...config,
			responseType: 'blob',
		})
	},
}
